import * as React from 'react'
import { useRef, useState, useEffect } from 'react'
import Head from 'next/head'
import { formatPhoneNumber } from '../utils/string-utils'
import { CityPhoneNumberData } from '../SEMPage/CityPhoneNumberData'
import { faCalendarCheck } from '@fortawesome/pro-duotone-svg-icons/faCalendarCheck'
import { faCarMechanic } from '@fortawesome/pro-duotone-svg-icons/faCarMechanic'
import { NextComponentType } from 'next'
import Layout from '../Layout'
import { ServicesHowItWorksSection } from './ServicesHowItWorksSection'
import { HappinessGuaranteeSection } from './HappinessGuaranteeSection'
import { twDefaultInitialWizardFormValues } from '../Forms/ScheduleServiceMultiStep/NoEmailScheduleServiceFormMultiStep'
import HeaderSection from './HeaderSection'
import { faMoneyCheckEditAlt } from '@fortawesome/pro-duotone-svg-icons/faMoneyCheckEditAlt'
import { WhyInspectionSection } from './WhyInspectionSection'
import { IncludedWithInspectionSection } from './IncludedWithInspectionSection'
import { NotYourTypicalProviderSection } from './NotYourTypicalProviderSection'
import { FAQSection } from './FAQSection'
import { GraySection } from '../GraySection'
import { ReviewsSection } from './ReviewsSection'
import { CtaButton } from './CtaButton'
import { LeadExtraFields } from '../Fields/LeadExtraFields'
import { ViocCopy, getViocCopy, viocCopyEs } from './viocCopy'

export type ViocIndexPageTemplateProps = {
  phoneNumber: string
  phoneNumberTxt: string
  rating: string
  reviewCount: number
  copy?: ViocCopy
}
export const ViocIndexPageTemplate: React.FC<ViocIndexPageTemplateProps> = ({
  phoneNumber,
  phoneNumberTxt,
  rating,
  reviewCount,
  copy: copyProp,
}) => {
  const copy = copyProp ?? getViocCopy('en')
  const layoutLocale = copy === viocCopyEs ? 'es' : 'en'

  // Show sticky CTA once the user scrolls past the header fold
  const headerRef = useRef<HTMLDivElement>(null)
  const [showStickyBar, setShowStickyBar] = useState(false)

  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <Layout
      navBarVariant="simple"
      showFooter={false}
      showGetQuote={false}
      phoneNumber={phoneNumber}
      phoneNumberTxt={phoneNumberTxt}
      title={copy.meta.title}
      locale={layoutLocale}
    >
      <Head>
        <meta name="description" key="description" content={copy.meta.description} />
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      {/* Sentinel: when this exits the viewport the sticky bar appears */}
      <div ref={headerRef}>
        <HeaderSection
          rating={rating}
          formInitialProps={{
            ...twDefaultInitialWizardFormValues,
            'form-name': 'semScheduleServiceForm',
          }}
          reviewCount={reviewCount}
          title={copy.header.title}
          subtitle={copy.header.subtitle}
          bullets={copy.header.bullets}
          placeholder={copy.header.placeholder}
          scheduleButton={copy.header.scheduleButton}
          reviewsLabel={copy.header.reviewsLabel}
        />
      </div>
      <div className="bg-gray-100">
        <div className="pt-10 w-full max-w-[700px] mx-auto px-5">
          <iframe
            className="w-full aspect-video"
            src="https://www.youtube.com/embed/qot_8qfLxTQ?si=s2W2uVjJUkZ_Wg1m"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </div>
      <WhyInspectionSection
        title={copy.whyInspection.title}
        description={copy.whyInspection.description}
        reasons={copy.whyInspection.reasons}
      />
      <IncludedWithInspectionSection
        title={copy.includedWithInspection.title}
        description={copy.includedWithInspection.description}
        items={copy.includedWithInspection.items}
      />
      <ServicesHowItWorksSection
        title={copy.howItWorks.title}
        steps={copy.howItWorks.steps.map((step, i) => ({
          ...step,
          image: [faMoneyCheckEditAlt, faCalendarCheck, faCarMechanic][i],
        }))}
        buttonText={copy.howItWorks.buttonText}
      />
      <ReviewsSection
        title={copy.reviews.title}
        partnerLabel={copy.reviews.partnerLabel}
        reviewCountLabel={copy.reviews.reviewCountLabel}
        first={copy.reviews.first}
        second={copy.reviews.second}
        third={copy.reviews.third}
      />
      <NotYourTypicalProviderSection
        title={copy.notYourTypical.title}
        description={copy.notYourTypical.description}
        items={copy.notYourTypical.items}
        buttonText={copy.notYourTypical.buttonText}
      />
      <FAQSection title={copy.faq.title} faqs={copy.faq.faqs} />
      <HappinessGuaranteeSection title={copy.happiness.title} description={copy.happiness.description} />
      <GraySection className="xl:py-20">
        <div className="flex w-full justify-center">
          <CtaButton href="#" className="w-full lg:w-fit" buttonText={copy.ctaButton} />
        </div>
      </GraySection>
      {/* Sticky CTA — slides up from bottom after scrolling past the fold */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          transform: showStickyBar ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-in-out',
        }}
      >
        <div className="bg-white border-t border-gray-200 shadow-lg px-4 py-3 flex justify-center">
          <CtaButton href="#" className="w-full max-w-sm" buttonText={copy.ctaButton} />
        </div>
      </div>

      <LeadExtraFields />
    </Layout>
  )
}

const ViocPage: NextComponentType<any, any, { rating: string; reviewCount: number; sessionId: string }> = ({
  rating = '4.94',
  reviewCount = 6013,
}) => {
  const phoneNumber = CityPhoneNumberData.generalValvoline.phoneNumber
  const phoneNumberTxt = formatPhoneNumber(phoneNumber)
  return (
    <ViocIndexPageTemplate
      rating={rating}
      reviewCount={reviewCount}
      phoneNumberTxt={phoneNumberTxt}
      phoneNumber={phoneNumber}
    />
  )
}

export default ViocPage
